import { useState } from 'react';
import SafeIcon from './SafeIcon';
import { channelFormats } from '../store/campaigns';
import { useApp } from '../i18n/AppContext';
import { useUi } from '../store/ui';
import { postStatusHoldsDailySlot, useWorkspaceData } from '../store/DemoStore';
import { DEMO_TODAY } from '../store/seed';
import type { Campaign, PlanSlot } from '../store/seed';
import type { Format, Post } from '../types';
import { channelIconName, useDayFormat } from './campaignDisplay';

const formatsAvailableForChannel: Record<PlanSlot['channel'], Format[]> = channelFormats;
const DEFAULT_CAMPAIGN_LENGTH_DAYS = 14;
const statusClassName: Record<string, string> = {
  Published: 'published',
  Scheduled: 'scheduled',
  'Needs your review': 'review',
  'AMY is working': 'ready',
  Cancelled: 'neutral',
  'Approved · Stopped': 'neutral',
};

export default function PlanEditor({
  campaign,
  plan,
  onChange,
  editable,
}: {
  campaign: Pick<Campaign, 'channels' | 'start' | 'end'>;
  plan: PlanSlot[];
  onChange: (plan: PlanSlot[]) => void;
  editable: boolean;
}) {
  const { translate } = useApp();
  const { route, navigate } = useUi();
  const { posts } = useWorkspaceData(route.workspaceId);
  const formatDay = useDayFormat();
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [draft, setDraft] = useState<PlanSlot | null>(null);
  const firstPlannableDay = Math.max(campaign.start ?? DEMO_TODAY + 1, DEMO_TODAY + 1);
  const lastPlannableDay = Math.max(
    campaign.end ?? firstPlannableDay + DEFAULT_CAMPAIGN_LENGTH_DAYS - 1,
    firstPlannableDay
  );
  const isDayTakenForSlot = (slot: PlanSlot, slotIndex: number, dayOfMonth: number) =>
    posts.some(
      (post) =>
        post.channel === slot.channel &&
        post.day === dayOfMonth &&
        postStatusHoldsDailySlot(post.status) &&
        post.id !== slot.postId
    ) ||
    plan.some(
      (otherSlot, otherIndex) =>
        otherIndex !== slotIndex &&
        otherSlot.channel === slot.channel &&
        otherSlot.day === dayOfMonth
    );
  const plannableDays = Array.from(
    { length: lastPlannableDay - firstPlannableDay + 1 },
    (_, offset) => firstPlannableDay + offset
  );
  const byDay = (firstSlot: PlanSlot, secondSlot: PlanSlot) => firstSlot.day - secondSlot.day;

  function startEdit(slotIndex: number) {
    setEditingIndex(slotIndex);
    setDraft({ ...plan[slotIndex] });
  }
  function stopEditing() {
    setEditingIndex(null);
    setDraft(null);
  }
  function saveDraft() {
    if (editingIndex === null || !draft) return;
    onChange(
      plan
        .map((slot, slotIndex) =>
          slotIndex === editingIndex ? { ...draft, title: draft.title.trim() || slot.title } : slot
        )
        .sort(byDay)
    );
    stopEditing();
  }
  function addSlotOnFirstFreeDay() {
    const channel = campaign.channels[0];
    const unsavedSlot: PlanSlot = { day: 0, channel, format: 'Photo', title: '' };
    const NOT_IN_PLAN = -1;
    const firstFreeDay = plannableDays.find(
      (dayOfMonth) => !isDayTakenForSlot(unsavedSlot, NOT_IN_PLAN, dayOfMonth)
    );
    if (!firstFreeDay) return;
    const updatedPlan = [
      ...plan,
      { day: firstFreeDay, channel, format: 'Photo' as Format, title: translate('New post') },
    ].sort(byDay);
    onChange(updatedPlan);
    const newSlotIndex = updatedPlan.findIndex(
      (slot) =>
        slot.day === firstFreeDay &&
        slot.channel === channel &&
        !slot.postId &&
        slot.title === translate('New post')
    );
    setEditingIndex(newSlotIndex);
    setDraft({ ...updatedPlan[newSlotIndex] });
  }

  return (
    <div className="plan-editor">
      <ol className="plan-list">
        {plan.map((slot, slotIndex) => {
          const post: Post | undefined = posts.find((item) => item.id === slot.postId);
          if (editingIndex === slotIndex && draft) {
            const draftDayIsTaken = isDayTakenForSlot(draft, slotIndex, draft.day);
            return (
              <li key={slotIndex} className="plan-edit-row">
                <select
                  className="select-field"
                  aria-label={translate('Day')}
                  value={draft.day}
                  onChange={(event) => setDraft({ ...draft, day: Number(event.target.value) })}
                >
                  {plannableDays.map((dayOfMonth) => {
                    const taken = isDayTakenForSlot(draft, slotIndex, dayOfMonth);
                    return (
                      <option key={dayOfMonth} value={dayOfMonth} disabled={taken}>
                        {formatDay(dayOfMonth)}
                        {taken ? ` · ${translate('taken')}` : ''}
                      </option>
                    );
                  })}
                </select>
                <select
                  className="select-field"
                  aria-label={translate('Channels')}
                  value={draft.channel}
                  onChange={(event) => {
                    const channel = event.target.value as PlanSlot['channel'];
                    setDraft({
                      ...draft,
                      channel,
                      format: formatsAvailableForChannel[channel].includes(draft.format)
                        ? draft.format
                        : formatsAvailableForChannel[channel][0],
                    });
                  }}
                >
                  {campaign.channels.map((campaignChannel) => (
                    <option key={campaignChannel}>{campaignChannel}</option>
                  ))}
                </select>
                <input
                  className="text-field"
                  aria-label={translate('Title')}
                  value={draft.title}
                  onChange={(event) => setDraft({ ...draft, title: event.target.value })}
                />
                <select
                  className="select-field"
                  aria-label={translate('Formats')}
                  value={draft.format}
                  onChange={(event) => setDraft({ ...draft, format: event.target.value as Format })}
                >
                  {formatsAvailableForChannel[draft.channel].map((format) => (
                    <option key={format} value={format}>
                      {translate(format)}
                    </option>
                  ))}
                </select>
                {draftDayIsTaken && (
                  <span className="field-warn plan-conflict">
                    {translate('{channel} already has a post on {day}. Choose another day.', {
                      channel: draft.channel,
                      day: formatDay(draft.day),
                    })}
                  </span>
                )}
                <span className="button-row">
                  <button
                    className="button small primary"
                    onClick={saveDraft}
                    disabled={draftDayIsTaken}
                  >
                    {translate('Save')}
                  </button>
                  <button className="button small ghost" onClick={stopEditing}>
                    {translate('Cancel')}
                  </button>
                </span>
              </li>
            );
          }
          return (
            <li key={slotIndex}>
              <span className="plan-day">{formatDay(slot.day)}</span>
              <SafeIcon name={channelIconName[slot.channel] as 'FiInstagram'} size={14} />
              <span className="plan-title">
                {slot.title}
                <span className="muted"> · {translate(slot.format)}</span>
              </span>
              {post ? (
                <button
                  className="link-button"
                  onClick={() => navigate({ page: 'post', id: post.id })}
                >
                  <span className={`status ${statusClassName[post.status] ?? 'neutral'}`}>
                    {translate(post.status)}
                  </span>
                </button>
              ) : editable ? (
                <span className="button-row plan-actions">
                  <button
                    className="icon-button small-icon"
                    aria-label={translate('Edit {word}', { word: slot.title })}
                    onClick={() => startEdit(slotIndex)}
                  >
                    <SafeIcon name="FiEdit2" size={14} />
                  </button>
                  <button
                    className="icon-button small-icon"
                    aria-label={translate('Remove {word}', { word: slot.title })}
                    onClick={() =>
                      onChange(plan.filter((_, otherIndex) => otherIndex !== slotIndex))
                    }
                  >
                    <SafeIcon name="FiX" size={14} />
                  </button>
                </span>
              ) : (
                <span className="status neutral">{translate('Planned')}</span>
              )}
            </li>
          );
        })}
      </ol>
      {editable && editingIndex === null && (
        <button
          className="button small"
          onClick={addSlotOnFirstFreeDay}
          disabled={!plannableDays.length}
        >
          <SafeIcon name="FiPlus" size={14} />
          {translate('Add a post to the plan')}
        </button>
      )}
    </div>
  );
}
